import React, { useState, useEffect } from 'react';
import { getPushPermissionStatus, requestPushPermission, syncWatchlistWithServer } from '../services/pushService';
import { isNative } from '../config/apiConfig';
import { useFreemium } from '../hooks/useFreemium';
import { setItem, removeItem } from '../services/iCloudStorage';
import './SettingsView.css';

const THEME_OPTIONS = [
  { value: 'light', label: 'Hell' },
  { value: 'system', label: 'System' },
  { value: 'dark', label: 'Dunkel' },
];

const SIGNAL_THRESHOLDS = [
  { value: 80, label: '80%' },
  { value: 90, label: '90%' },
  { value: 95, label: '95%' },
];

const MARKET_OPTIONS = [
  { value: 'us',     label: 'USA' },
  { value: 'de',     label: 'Deutschland' },
  { value: 'eu',     label: 'Europa' },
  { value: 'crypto', label: 'Krypto' },
  { value: 'japan',  label: 'Japan' },
];

const SETTINGS_MARKETS_KEY    = 'bullivo_markets';
const SETTINGS_THRESHOLD_KEY  = 'bullivo_signal_threshold';
const ONBOARDING_KEY          = 'bullivo_onboarding_done';
const REALTIME_ALERTS_KEY     = 'bullivo_realtime_alerts';
const NOTIFY_FIXED_KEY        = 'bullivo_notify_fixed';
const NOTIFY_TOP3_KEY         = 'bullivo_notify_top3';
const NOTIFY_WATCHLIST_KEY    = 'bullivo_notify_watchlist';
const NOTIFY_WHALE_KEY        = 'bullivo_notify_whale';
const REBOUND_MODE_KEY        = 'bullivo_rebound_mode';
const PREFERRED_SECTORS_KEY   = 'bullivo_preferred_sectors_v1';

const SECTOR_OPTIONS = [
  { id: 'Technology', label: 'Technologie' },
  { id: 'Healthcare', label: 'Gesundheit' },
  { id: 'Financial Services', label: 'Finanzen' },
  { id: 'Industrials', label: 'Industrie' },
  { id: 'Consumer Cyclical', label: 'Konsum (zykl.)' },
  { id: 'Consumer Defensive', label: 'Konsum (defens.)' },
  { id: 'Communication Services', label: 'Kommunikation' },
  { id: 'Energy', label: 'Energie' },
  { id: 'Utilities', label: 'Versorger' },
  { id: 'Real Estate', label: 'Immobilien' },
  { id: 'Basic Materials', label: 'Rohstoffe' },
];

function loadPreferredSectors() {
  try {
    const v = localStorage.getItem(PREFERRED_SECTORS_KEY);
    return v ? JSON.parse(v) : [];
  } catch { return []; }
}

function loadMarkets() {
  try {
    const v = localStorage.getItem(SETTINGS_MARKETS_KEY);
    return v ? JSON.parse(v) : ['us', 'de', 'eu', 'crypto', 'japan'];
  } catch { return ['us', 'de', 'eu', 'crypto', 'japan']; }
}

function loadThreshold() {
  try { return Number(localStorage.getItem(SETTINGS_THRESHOLD_KEY)) || 80; } catch { return 80; }
}

// Simple SVG icons to replace emojis
function Icon({ name, color = 'blue' }) {
  const icons = {
    notifications: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>,
    morning: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58a.996.996 0 0 0-1.41 0 .996.996 0 0 0 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37a.996.996 0 0 0-1.41 0 .996.996 0 0 0 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0a.996.996 0 0 0 0-1.41l-1.06-1.06zm1.06-10.96a.996.996 0 0 0 0-1.41.996.996 0 0 0-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36a.996.996 0 0 0 0-1.41.996.996 0 0 0-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z"/></svg>,
    star: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>,
    visibility: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>,
    whale: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z"/></svg>,
    equalizer: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20h4V4h-4v16zm-6 0h4v-8H4v8zM16 9v11h4V9h-4z"/></svg>,
    palette: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-1 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>,
    person: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>,
    info: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>,
    help: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>,
    mail: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>,
    shield: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/></svg>,
    globe: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z"/></svg>,
    chevron: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>,
  };
  return <span className={`sv-icon ${color}`}>{icons[name] || null}</span>;
}

function SettingsView({ theme, setTheme, userName, onShowPaywall, assets = [], onClose }) {
  const [permission, setPermission]     = useState('prompt');
  const [markets, setMarkets]           = useState(loadMarkets);
  const [threshold, setThreshold]       = useState(loadThreshold);
  const [alertsEnabled, setAlertsEnabled] = useState(() => {
    try { const v = localStorage.getItem(REALTIME_ALERTS_KEY); return v === null ? true : v === 'true'; } catch { return true; }
  });
  const [preferredSectors, setPreferredSectors] = useState(loadPreferredSectors);
  const togglePreferredSector = (id) => {
    setPreferredSectors(prev => {
      const next = prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id];
      try { localStorage.setItem(PREFERRED_SECTORS_KEY, JSON.stringify(next)); } catch {}
      try { window.dispatchEvent(new CustomEvent('bullivo:preferredSectorsChanged')); } catch {}
      return next;
    });
  };

  const [notifyFixed, setNotifyFixed] = useState(() => {
    try { const v = localStorage.getItem(NOTIFY_FIXED_KEY); return v === null ? true : v === 'true'; } catch { return true; }
  });
  const [notifyTop3, setNotifyTop3] = useState(() => {
    try { const v = localStorage.getItem(NOTIFY_TOP3_KEY); return v === null ? true : v === 'true'; } catch { return true; }
  });
  const [notifyWatchlist, setNotifyWatchlist] = useState(() => {
    try { const v = localStorage.getItem(NOTIFY_WATCHLIST_KEY); return v === null ? true : v === 'true'; } catch { return true; }
  });
  const [notifyWhale, setNotifyWhale] = useState(() => {
    try { const v = localStorage.getItem(NOTIFY_WHALE_KEY); return v === null ? true : v === 'true'; } catch { return true; }
  });
  const [reboundMode, setReboundMode] = useState(() => {
    try { return localStorage.getItem(REBOUND_MODE_KEY) === '1'; } catch { return false; }
  });

  const toggleReboundMode = () => {
    const next = !reboundMode;
    setReboundMode(next);
    try { localStorage.setItem(REBOUND_MODE_KEY, next ? '1' : '0'); } catch {}
    try { localStorage.removeItem('bullivo_discover_ranked_v1'); } catch {}
    window.dispatchEvent(new Event('bullivo:rebound-toggle'));
  };
  const { isPremium, freemiumActive } = useFreemium();

  useEffect(() => { getPushPermissionStatus().then(setPermission); }, []);

  const toggleMarket = (val) => {
    const next = markets.includes(val) ? markets.filter(m => m !== val) : [...markets, val];
    if (next.length === 0) return;
    setMarkets(next);
    setItem(SETTINGS_MARKETS_KEY, JSON.stringify(next));
  };

  const handleThreshold = (val) => {
    setThreshold(val);
    setItem(SETTINGS_THRESHOLD_KEY, String(val));
  };

  const syncAll = (opts) => {
    if (assets.length > 0) syncWatchlistWithServer(assets, opts.alerts ?? alertsEnabled, {
      fixed: opts.fixed ?? notifyFixed, top3: opts.top3 ?? notifyTop3,
      watchlist: opts.watchlist ?? notifyWatchlist, whale: opts.whale ?? notifyWhale,
    });
  };

  const handleRequestPermission = async () => {
    const granted = await requestPushPermission();
    setPermission(granted ? 'granted' : 'denied');
  };

  const resetOnboarding = () => {
    removeItem(ONBOARDING_KEY);
    window.location.reload();
  };

  return (
    <div className="sv-wrap">
      <div className="sv-title-row">
        <h2 className="sv-title">Einstellungen</h2>
        {onClose && (
          <button className="sv-close" onClick={onClose} aria-label="Schließen">✕</button>
        )}
      </div>

      {/* ── BENACHRICHTIGUNGEN ── */}
      <div className="sv-section">
        <h3 className="sv-section-title">Benachrichtigungen</h3>
        <div className="sv-group">
          <div className="sv-row">
            <div className="sv-row-left">
              <Icon name="notifications" color="blue" />
              <div className="sv-row-info">
                <span className="sv-row-label">Push-Benachrichtigungen</span>
                <span className="sv-row-desc">
                  {permission === 'granted' && 'Aktiv'}
                  {permission === 'denied' && 'In iPhone-Einstellungen aktivieren'}
                  {permission === 'prompt' && 'Noch nicht aktiviert'}
                  {permission === 'not-supported' && 'Nicht unterstützt'}
                </span>
              </div>
            </div>
            {permission === 'prompt' && <button className="sv-btn" onClick={handleRequestPermission}>Aktivieren</button>}
            {permission === 'granted' && <span className="sv-badge granted">AN</span>}
            {permission === 'denied'  && <span className="sv-badge denied">AUS</span>}
          </div>

          {permission === 'granted' && isPremium && (<>
            <div className="sv-divider" />
            <div className="sv-row">
              <div className="sv-row-left">
                <Icon name="morning" color="blue" />
                <span className="sv-row-label">Morning & Late Update</span>
              </div>
              <button className={`sv-toggle${notifyFixed ? ' on' : ''}`}
                onClick={() => { setNotifyFixed(!notifyFixed); setItem(NOTIFY_FIXED_KEY, String(!notifyFixed)); syncAll({ fixed: !notifyFixed }); }} />
            </div>
            <div className="sv-divider" />
            <div className="sv-row">
              <div className="sv-row-left">
                <Icon name="star" color="blue" />
                <span className="sv-row-label">Top-3 Signale</span>
              </div>
              <button className={`sv-toggle${notifyTop3 ? ' on' : ''}`}
                onClick={() => { setNotifyTop3(!notifyTop3); setItem(NOTIFY_TOP3_KEY, String(!notifyTop3)); syncAll({ top3: !notifyTop3 }); }} />
            </div>
          </>)}
          {permission === 'granted' && freemiumActive && !isPremium && (<>
            <div className="sv-divider" />
            <div className="sv-row" onClick={() => onShowPaywall?.()} style={{ cursor: 'pointer' }}>
              <div className="sv-row-left">
                <Icon name="notifications" color="gray" />
                <div className="sv-row-info">
                  <span className="sv-row-label">Push-Alerts</span>
                  <span className="sv-row-desc">Mit Premium freischalten</span>
                </div>
              </div>
              <span className="sv-badge denied">🔒</span>
            </div>
          </>)}
        </div>
      </div>

      {/* ── MÄRKTE ── */}
      <div className="sv-section">
        <h3 className="sv-section-title">Märkte</h3>
        <div className="sv-group">
          {(!freemiumActive || isPremium) ? (<>
          <div className="sv-row">
            <div className="sv-row-left">
              <Icon name="visibility" color="blue" />
              <span className="sv-row-label">Watchlist-Signalwechsel</span>
            </div>
            <button className={`sv-toggle${notifyWatchlist ? ' on' : ''}`}
              onClick={() => {
                const next = !notifyWatchlist;
                setNotifyWatchlist(next); setAlertsEnabled(next);
                setItem(NOTIFY_WATCHLIST_KEY, String(next)); setItem(REALTIME_ALERTS_KEY, String(next));
                syncAll({ watchlist: next, alerts: next });
              }} />
          </div>
          <div className="sv-divider" />
          <div className="sv-row">
            <div className="sv-row-left">
              <Icon name="whale" color="blue" />
              <span className="sv-row-label">Whale-Alarm</span>
            </div>
            <button className={`sv-toggle${notifyWhale ? ' on' : ''}`}
              onClick={() => { setNotifyWhale(!notifyWhale); setItem(NOTIFY_WHALE_KEY, String(!notifyWhale)); syncAll({ whale: !notifyWhale }); }} />
          </div>
          </>) : (
          <div className="sv-row" onClick={() => onShowPaywall?.()} style={{ cursor: 'pointer' }}>
            <div className="sv-row-left">
              <Icon name="notifications" color="gray" />
              <div className="sv-row-info">
                <span className="sv-row-label">Watchlist & Whale Alerts</span>
                <span className="sv-row-desc">Mit Premium freischalten</span>
              </div>
            </div>
            <span className="sv-badge denied">🔒</span>
          </div>
          )}
          <div className="sv-divider" />
          {/* Signalstärke-Schwelle */}
          <div style={{ padding: '14px 16px 4px' }}>
            <div className="sv-row-left" style={{ marginBottom: 12 }}>
              <Icon name="equalizer" color="blue" />
              <span className="sv-row-label">Signalstärke-Schwelle</span>
            </div>
            <div className="sv-segment">
              {SIGNAL_THRESHOLDS.map(opt => (
                <button key={opt.value}
                  className={`sv-segment-btn${threshold === opt.value ? ' active' : ''}`}
                  onClick={() => handleThreshold(opt.value)}>
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="sv-segment-hint">Nur Signale ab diesem Confidence-Wert anzeigen.</p>
          </div>
          <div className="sv-divider" />
          {(!freemiumActive || isPremium) ? (
            <div className="sv-row">
              <div className="sv-row-left">
                <Icon name="equalizer" color="blue" />
                <div className="sv-row-info">
                  <span className="sv-row-label">Rebound-Modus</span>
                  <span className="sv-row-desc">Aggressive Picks an der unteren Schwelle. Höheres Risiko, kurze Haltedauer.</span>
                </div>
              </div>
              <button className={`sv-toggle${reboundMode ? ' on' : ''}`} onClick={toggleReboundMode} />
            </div>
          ) : (
            <div className="sv-row" onClick={() => onShowPaywall?.()} style={{ cursor: 'pointer' }}>
              <div className="sv-row-left">
                <Icon name="equalizer" color="gray" />
                <div className="sv-row-info">
                  <span className="sv-row-label">Rebound-Modus</span>
                  <span className="sv-row-desc">Mit Premium freischalten</span>
                </div>
              </div>
              <span className="sv-badge denied">🔒</span>
            </div>
          )}
          <div style={{ height: 8 }} />
        </div>

        <div style={{ height: 12 }} />
        <div className="sv-markets">
          {MARKET_OPTIONS.map(m => (
            <button key={m.value}
              className={`sv-market-btn${markets.includes(m.value) ? ' active' : ''}`}
              onClick={() => toggleMarket(m.value)}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── BEVORZUGTE BRANCHEN ── */}
      <div className="sv-section">
        <h3 className="sv-section-title">Bevorzugte Branchen</h3>
        <div className="sv-group">
          <div style={{ padding: '14px 16px 6px' }}>
            <p className="sv-segment-hint">
              Aktien aus markierten Branchen werden im Signale-Tab zuerst angezeigt. Die Quality-Bewertung selbst aendert sich nicht.
            </p>
          </div>
          <div style={{ height: 8 }} />
          <div className="sv-markets">
            {SECTOR_OPTIONS.map(s => (
              <button key={s.id}
                className={`sv-market-btn${preferredSectors.includes(s.id) ? ' active' : ''}`}
                onClick={() => togglePreferredSector(s.id)}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── DARSTELLUNG ── */}
      <div className="sv-section">
        <h3 className="sv-section-title">Darstellung</h3>
        <div className="sv-group">
          <div style={{ padding: '14px 16px 4px' }}>
            <div className="sv-row-left" style={{ marginBottom: 12 }}>
              <Icon name="palette" color="blue" />
              <div className="sv-row-info">
                <span className="sv-row-label">Erscheinungsbild</span>
                <span className="sv-row-desc">
                  {theme === 'system' ? 'Folgt den iOS-Einstellungen' : theme === 'dark' ? 'Immer dunkel' : 'Immer hell'}
                </span>
              </div>
            </div>
            <div className="sv-segment">
              {THEME_OPTIONS.map(opt => (
                <button key={opt.value}
                  className={`sv-segment-btn${theme === opt.value ? ' active' : ''}`}
                  onClick={() => setTheme(opt.value)}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ height: 12 }} />
        </div>
      </div>

      {/* ── ACCOUNT ── */}
      <div className="sv-section">
        <h3 className="sv-section-title">Account</h3>
        <div className="sv-group">
          <div className="sv-row">
            <div className="sv-row-left">
              <Icon name="person" color="gray" />
              <div className="sv-row-info">
                <span className="sv-row-label">{userName || 'Nicht gesetzt'}</span>
                <span className="sv-row-desc">Über iCloud synchronisiert</span>
              </div>
            </div>
          </div>
          {freemiumActive && (<>
            <div className="sv-divider" />
            <div className="sv-row">
              <div className="sv-row-left">
                <Icon name="star" color={isPremium ? 'green' : 'gray'} />
                <div className="sv-row-info">
                  <span className="sv-row-label">Abo-Status</span>
                  <span className="sv-row-desc">{isPremium ? 'Bullivo Pro aktiv' : 'Free-Version'}</span>
                </div>
              </div>
              {isPremium
                ? <span className="sv-badge granted">PRO</span>
                : <button className="sv-btn" onClick={onShowPaywall}>Upgrade</button>}
            </div>
          </>)}
        </div>
      </div>

      {/* ── APP ── */}
      <div className="sv-section">
        <h3 className="sv-section-title">App</h3>
        <div className="sv-group">
          <div className="sv-row">
            <div className="sv-row-left">
              <Icon name="info" color="gray" />
              <div className="sv-row-info">
                <span className="sv-row-label">Bullivo 1.5.0</span>
                <span className="sv-row-desc">Signal Engine v4</span>
              </div>
            </div>
          </div>
          <div className="sv-divider" />
          <button className="sv-link" onClick={() => window.open('mailto:bullivo.support@gmail.com')}>
            <div className="sv-row-left">
              <Icon name="mail" color="gray" />
              <span className="sv-row-label">Feedback senden</span>
            </div>
            <Icon name="chevron" color="gray" />
          </button>
          {isNative() && (<>
            <div className="sv-divider" />
            <button className="sv-link" onClick={() => window.open('itms-apps://itunes.apple.com/app/id6760433372?action=write-review')}>
              <div className="sv-row-left">
                <Icon name="star" color="gray" />
                <span className="sv-row-label">App bewerten</span>
              </div>
              <Icon name="chevron" color="gray" />
            </button>
          </>)}
          <div className="sv-divider" />
          <button className="sv-link" onClick={resetOnboarding}>
            <div className="sv-row-left">
              <Icon name="help" color="gray" />
              <span className="sv-row-label">Einführung erneut anzeigen</span>
            </div>
            <Icon name="chevron" color="gray" />
          </button>
        </div>
      </div>

      {/* ── RECHTLICHES ── */}
      <div className="sv-section">
        <h3 className="sv-section-title">Rechtliches</h3>
        <div className="sv-group">
          <button className="sv-link" onClick={() => window.open('https://bullivo.de/disclaimer.html')}>
            <div className="sv-row-left">
              <Icon name="shield" color="gray" />
              <span className="sv-row-label">Haftungsausschluss</span>
            </div>
            <Icon name="chevron" color="gray" />
          </button>
          <div className="sv-divider" />
          <button className="sv-link" onClick={() => window.open('https://bullivo.de/privacy.html')}>
            <div className="sv-row-left">
              <Icon name="shield" color="gray" />
              <span className="sv-row-label">Datenschutzerklärung</span>
            </div>
            <Icon name="chevron" color="gray" />
          </button>
          <div className="sv-divider" />
          <button className="sv-link" onClick={() => window.open('https://www.apple.com/legal/internet-services/itunes/dev/stdeula/')}>
            <div className="sv-row-left">
              <Icon name="shield" color="gray" />
              <span className="sv-row-label">Nutzungsbedingungen</span>
            </div>
            <Icon name="chevron" color="gray" />
          </button>
          <div className="sv-divider" />
          <button className="sv-link" onClick={() => window.open('https://bullivo.de/imprint.html')}>
            <div className="sv-row-left">
              <Icon name="globe" color="gray" />
              <span className="sv-row-label">Impressum</span>
            </div>
            <Icon name="chevron" color="gray" />
          </button>
        </div>
      </div>

      {/* ── GLOSSAR ── */}
      <div className="sv-section">
        <h3 className="sv-section-title">Glossar</h3>
        <div className="sv-group">
          <GlossarItem term="Score" desc="Die Gesamtbewertung eines Signals von 0–100%. Setzt sich zusammen aus technischen Indikatoren (RSI, 52W-Position), Bewertungskennzahlen (KGV, KCV), KI-Analyse und Momentum. Je mehr Faktoren in dieselbe Richtung zeigen, desto höher der Score." />
          <div className="sv-divider" />
          <GlossarItem term="KGV (Kurs-Gewinn-Verhältnis)" desc="Aktienkurs geteilt durch Gewinn pro Aktie. Zeigt, wie viele Jahre es dauern würde, bis sich der Kaufpreis durch Gewinne amortisiert. Niedrig (< 15) = günstig, hoch (> 35) = teuer." />
          <div className="sv-divider" />
          <GlossarItem term="KCV (Kurs-Cashflow-Verhältnis)" desc="Aktienkurs geteilt durch den operativen Cashflow pro Aktie. Misst, wie viel echtes Geld ein Unternehmen generiert. Oft aussagekräftiger als KGV, da Cashflow schwerer zu manipulieren ist. Niedrig (< 10) = stark." />
          <div className="sv-divider" />
          <GlossarItem term="Bewertung (KGV/KCV)" desc="Das Verhältnis von KGV zu KCV. Zeigt die Qualität der Gewinne: Liegt der Wert unter 1, verdient das Unternehmen mehr Cash als der Buchgewinn zeigt — ein gutes Zeichen. Über 1,5 deutet auf aufgeblähte Gewinne hin." />
          <div className="sv-divider" />
          <GlossarItem term="RSI (Relative Strength Index)" desc="Technischer Indikator von 0–100, der die Stärke der Kursbewegung misst. Unter 30 = überverkauft (mögliche Kaufgelegenheit), über 70 = überkauft (mögliche Korrektur). Bullivo nutzt RSI-14 (14 Tage)." />
          <div className="sv-divider" />
          <GlossarItem term="52W-Position" desc="Wo steht der aktuelle Kurs im Vergleich zum 52-Wochen-Hoch und -Tief? 0% = am Jahrestief, 100% = am Jahreshoch. Aktien nahe dem Tief können Einstiegschancen bieten, nahe dem Hoch ist Vorsicht geboten." />
          <div className="sv-divider" />
          <GlossarItem term="EMA-Crossover" desc="Wenn der kurzfristige Durchschnitt (8 Tage) den langfristigen (21 Tage) kreuzt. Bullish = von unten nach oben (Aufwärtstrend beginnt), Bearish = von oben nach unten (Abwärtstrend beginnt)." />
          <div className="sv-divider" />
          <GlossarItem term="KI-Analyse" desc="Täglich um 08:00 analysiert eine KI alle 190 Ticker und identifiziert Trends in drei Zeithorizonten: kurzfristig (1–4 Wochen), mittelfristig (3–12 Monate) und langfristig (1–3 Jahre). Die Picks fließen mit +3 Punkten in den Score ein." />
          <div className="sv-divider" />
          <GlossarItem term="Signalstärke-Schwelle" desc="Der Mindest-Score, ab dem eine Aktie als Signal angezeigt wird. Bei 40% siehst du mehr Signale (auch schwächere), bei 80% nur die stärksten Überzeugungen." />
        </div>
      </div>

    </div>
  );
}

function GlossarItem({ term, desc }) {
  return (
    <div style={{ padding: '14px 16px' }}>
      <span className="sv-row-label">{term}</span>
      <p className="sv-row-desc" style={{ marginTop: 4, lineHeight: 1.5 }}>{desc}</p>
    </div>
  );
}

export default SettingsView;
